/**
 * /transport/profile/:slug — Public transporter profile (Phase 4)
 * ---------------------------------------------------------------
 * Anonymous-safe profile detail page. Shows hero photo, service area,
 * insurance summary, recent anonymized trip history, and a "Request
 * this transporter" CTA that pre-fills the buyer request form.
 */
import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import AppShell from "@/components/AppShell";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { TruckIcon as Truck, ShieldCheckIcon as ShieldCheck, MapPinIcon as MapPin, CheckCircleIcon as CheckCircle, StarIcon as Star } from "@phosphor-icons/react";

export default function TransportProfile() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api.get(`/transport/profile/${slug}`)
      .then((r) => setData(r.data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <AppShell>
        <div className="max-w-3xl mx-auto px-4 py-10 space-y-4">
          <Skeleton className="h-64" /><Skeleton className="h-40" />
        </div>
      </AppShell>
    );
  }
  if (notFound || !data) {
    return (
      <AppShell>
        <div className="max-w-2xl mx-auto px-4 py-16 text-center" data-testid="profile-not-found">
          <Truck size={48} weight="duotone" className="text-[#9A9A9A] mx-auto mb-3" />
          <h1 className="font-spectral text-3xl mb-2">Transporter not found</h1>
          <p className="text-[14px] text-[#6B6B6B] mb-4">
            The link may have expired or this carrier may no longer be active.
          </p>
          <Link to="/transport">
            <Button className="rounded-full bg-ink text-white">Browse directory</Button>
          </Link>
        </div>
      </AppShell>
    );
  }

  const t = data.transporter;
  const recent = data.recent_trips || [];
  const prefs = data.preferences || {};

  return (
    <AppShell>
      <div className="max-w-3xl mx-auto px-4 py-8" data-testid="transport-profile">
        <Link to="/transport" className="text-[12px] text-[#6B6B6B] hover:text-ink">
          ← Back to directory
        </Link>

        {/* Hero */}
        {t.rig_photo_url ? (
          <div
            className="h-64 rounded-2xl bg-cover bg-center mt-3 mb-4 border border-[#E5E5E5]"
            style={{ backgroundImage: `url(${t.rig_photo_url})` }}
            data-testid="hero-photo"
          />
        ) : (
          <div className="h-64 rounded-2xl bg-gradient-to-br from-[#F4EFE6] to-[#E8E2D2] mt-3 mb-4 flex items-center justify-center border border-[#E5E5E5]">
            <Truck size={64} weight="duotone" className="text-[#9A8B6E]" />
          </div>
        )}

        <div className="flex flex-wrap items-baseline justify-between gap-2 mb-1">
          <h1 className="font-spectral text-4xl tracking-tight">{t.display_name}</h1>
          {t.verified_badge_at && (
            <span className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wider font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2.5 py-1 rounded-full">
              <ShieldCheck size={12} weight="duotone" /> Armstrong-verified
            </span>
          )}
        </div>
        <p className="text-[13px] text-[#6B6B6B] mb-4 flex items-center gap-1.5">
          <MapPin size={13} weight="duotone" /> Service area:{" "}
          {(t.service_area_states || []).join(" · ")}
        </p>

        {/* Stat strip */}
        <div className="grid grid-cols-3 gap-3 mb-6">
          <Stat label="Trips completed" value={t.trips_completed ?? 0} icon={<Truck size={18} weight="duotone" />} />
          <Stat label="Liability coverage" value={t.insurance_liability_usd ? `$${(t.insurance_liability_usd / 1000).toFixed(0)}K` : "—"} icon={<ShieldCheck size={18} weight="duotone" />} />
          <Stat label="Insurance carrier" value={t.insurance_carrier || "—"} icon={<CheckCircle size={18} weight="duotone" />} />
        </div>

        {/* Preferences if set */}
        {(prefs.preferred_lanes || []).length > 0 && (
          <section className="border border-[#E5E5E5] bg-white rounded-2xl p-5 mb-4" data-testid="preferred-lanes">
            <h2 className="font-spectral text-xl tracking-tight mb-2">Preferred lanes</h2>
            <ul className="text-[13px] text-[#444] space-y-1">
              {prefs.preferred_lanes.map((l) => (
                <li key={l}>· {l}</li>
              ))}
            </ul>
          </section>
        )}
        {(prefs.breed_familiarity || []).length > 0 && (
          <section className="border border-[#E5E5E5] bg-white rounded-2xl p-5 mb-4" data-testid="breed-familiarity">
            <h2 className="font-spectral text-xl tracking-tight mb-2">Breed familiarity</h2>
            <p className="text-[13px] text-[#444] capitalize">
              {prefs.breed_familiarity.join(" · ")}
            </p>
          </section>
        )}

        {/* Recent trips — anonymized */}
        {recent.length > 0 && (
          <section className="border border-[#E5E5E5] bg-white rounded-2xl p-5 mb-4" data-testid="recent-trips">
            <h2 className="font-spectral text-xl tracking-tight mb-3">Recent completed trips</h2>
            <ul className="space-y-2">
              {recent.map((r, i) => (
                <li key={i} className="flex items-center justify-between gap-2 text-[13px] text-[#444] py-1.5 border-b border-[#EAEAEA] last:border-0">
                  <span className="flex items-center gap-1.5">
                    <MapPin size={11} weight="duotone" className="text-[#9A9A9A]" />
                    {r.route}
                  </span>
                  <span className="text-[11px] text-[#9A9A9A] flex items-center gap-2">
                    {r.month}
                    {r.rating && (
                      <span className="inline-flex items-center gap-0.5 text-amber-700">
                        <Star size={11} weight="fill" />{r.rating}
                      </span>
                    )}
                  </span>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* CTA */}
        <Link to="/transport/request" data-testid="profile-cta-request">
          <Button className="w-full rounded-full bg-crimson hover:bg-ink text-white font-semibold py-3">
            Request a transport → multiple haulers will bid
          </Button>
        </Link>
        <p className="text-[11px] text-[#9A9A9A] text-center mt-2 leading-relaxed">
          You can't book a specific transporter directly — every request fans out to all
          verified haulers in your route's service area and you pick the winning bid.
          This keeps prices competitive.
        </p>
      </div>
    </AppShell>
  );
}

function Stat({ label, value, icon }) {
  return (
    <div className="border border-[#E5E5E5] rounded-xl p-3 bg-[#FAFAFA]">
      <div className="flex items-center gap-1.5 text-[#6B6B6B] mb-0.5">
        {icon}
        <p className="text-[10px] uppercase tracking-wider font-bold">{label}</p>
      </div>
      <p className="font-spectral text-2xl tracking-tight">{value}</p>
    </div>
  );
}
